import React from "react"
import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles"
import {
  Dialog,
  useMediaQuery,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Divider,
  Link,
} from "@material-ui/core"
import "github-markdown-css"
import MDReactComponent from "markdown-react-js"
import MarkdownItMultimdTable from "markdown-it-multimd-table"
import { FormattedMessage } from "react-intl"
import clsx from "clsx"
import { backgroundFixed } from "utils/backgroundFixed.js"
import NewWindowIcon from "assets/images/buttons/NewWindowiconGray.svg"

const useStyles = makeStyles(theme => ({
  paperFullWidth: {},
  card: {
    left: "36px",
    top: "36px",
    height: "100%",
    position: "absolute",
    width: "calc(100vw - 72px)",
    backgroundColor: theme.palette.background.paper,
  },
  agreementmodalClose: {
    textTransform: "none",
  },
  cardHeader: {
    backgroundColor: "#FFF",
    minHeight: "51px",
    paddingLeft: "30px",
    paddingRight: "30px",
    paddingTop: "4px",
    paddingBottom: "4px",
    whiteSpace: "pre-line",
  },
  cardHeaderContainer: {
    width: "100%",
  },
  cardHeaderTitle: {
    fontSize: "14px",
    wordBreak: "normal",
    color: "rgba(0,0,0,0.85)",
    fontWeight: 600,
    textAlign: "center",
    lineHeight: "21px",
  },
  divider: {
    height: "2px",
    marginLeft: "8px",
    marginRight: "8px",
    borderRadius: "8px",
    backgroundColor: "#D2203E",
  },
  content: {
    [theme.breakpoints.down("xs")]: {
      maxHeight: "calc(100vh - 366px)",
    },
    maxHeight: "calc(100vh - 302px)",
    whiteSpace: "pre-line",
    wordBreak: "normal",
    paddingLeft: "23px",
    paddingRight: "23px",
    paddingTop: "30px",
    paddingBottom: "30px",
    "& .markdown-body": {
      color: "rgba(0, 0, 0, 0.54)",
    },
    "& .markdown-body h1, h2, h3, h4, h5, h6": {
      color: "rgba(0, 0, 0, 0.54)",
    },
    [theme.breakpoints.down("xs")]: {
      "& .markdown-body table": {
        whiteSpace: "pre",
      },
    },
  },
  sImg: {
    width: "25%",
    maxWidth: "100%",
    height: "auto",
  },
  mImg: {
    width: "50%",
    maxWidth: "100%",
    height: "auto",
  },
  lImg: {
    width: "100%",
    maxWidth: "100%",
    height: "auto",
  },
  img: {
    maxWidth: "100%",
    height: "auto",
  },
  center: {
    width: "100%",
    textAlign: "center",
    display: "block",
  },
  left: {
    width: "100%",
    textAlign: "left",
    display: "block",
  },
  right: {
    width: "100%",
    textAlign: "right",
    display: "block",
  },
  previewLinkColor: {
    "&&": {
      color: theme.palette.primary.main,
    },
  },
  newWindowIconInLink: {
    marginLeft: "3px",
    verticalAlign: "middle",
    position: "relative",
    top: "2px",
  },
}))

function useWidth() {
  const theme = useTheme()
  const keys = [...theme.breakpoints.keys].reverse()
  return (
    keys.reduce((output, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useMediaQuery(theme.breakpoints.up(key))
      return !output && matches ? key : output
    }, null) || "xs"
  )
}

const StyledDialog = withStyles({
  paperFullWidth: {
    width: "calc(100vw - 30px)",
    maxWidth: "960px",
  },
  paper: {
    marginLeft: "15px",
    marginRight: "15px",
    marginTop: "34px",
    borderRadius: "8px",
  },
})(Dialog)

const AgreementModal = ({ open, onClose, card }) => {
  const classes = useStyles()
  const maxWidth = useWidth()
  const tagSetting = {
    html: "div",
  }
  const handleIterate = (Tag, props, children) => {
    if (props.colspan) {
      props = {
        ...props,
        colSpan: props.colspan,
      }
      delete props.colspan
    }
    if (props.rowspan) {
      props = {
        ...props,
        rowSpan: props.rowspan,
      }
      delete props.rowspan
    }
    if (Tag === "img") {
      let classNameSize = "img"
      let classNameAlign = "left"
      let alt = props.alt
      if (props.alt) {
        const altArray = props.alt.split(":")
        if (altArray.length === 2) {
          const confArray = altArray[0].split("-")
          confArray.forEach(element => {
            classNameSize = ["s", "m", "l"].includes(element)
              ? `${element}Img`
              : classNameSize
            classNameAlign = ["center", "right"].includes(element)
              ? `${element}`
              : classNameAlign
          })
          alt = altArray[1]
        }
      }
      return (
        <span key={props.key} className={classes[`${classNameAlign}`]}>
          <img
            key={props.key}
            alt={alt}
            title={alt}
            src={`${process.env.REACT_APP_REFERENCE_DESTINATION}/${props.src}`}
            className={classes[`${classNameSize}`]}
          />
        </span>
      )
    } else if (Tag === "a") {
      return (
        <span key={props.key}>
          <Link
            key={props.key}
            href={props.href}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.previewLinkColor}
          >
            {children && children.length > 0 ? children : props.href}
            <span className={classes.newWindowIconInLink}>
              <img src={NewWindowIcon} alt="new window icon" />
            </span>
          </Link>
        </span>
      )
    } else if (Tag === "th" || Tag === "td") {
      const lineCellChildren = children.map(cell => {
        return typeof cell === "string" ? cell.replace(/\\n/g, "\n") : cell
      })
      children = lineCellChildren
    }
    return <Tag {...props}>{children}</Tag>
  }

  return (
    <StyledDialog
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
      TransitionProps={{ onEnter: () => backgroundFixed(true) }}
      aria-labelledby="max-width-dialog-title"
      className="agreement-modal"
    >
      <Grid
        container
        direction="row"
        justify="center"
        alignItems="center"
        className={classes.cardHeader}
        id="agreementModalName"
      >
        <Grid item className={classes.cardHeaderContainer}>
          <Typography
            className={classes.cardHeaderTitle}
            data-testid="agreementmodal-name"
          >
            {card.name}
          </Typography>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      <DialogContent className={classes.content} id="agreementModalContent">
        <MDReactComponent
          className="markdown-body"
          text={card.detail || ""}
          onIterate={handleIterate}
          plugins={[
            { plugin: MarkdownItMultimdTable, args: [{ rowspan: true }] },
          ]}
          tags={tagSetting}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          fullWidth
          onClick={onClose}
          color="primary"
          data-testid="agreementmodal-close"
          className={clsx("agreementmodal-close", classes.agreementmodalClose)}
          id="agreementModalCloseButton"
        >
          <FormattedMessage id="AgreementModal.CloseButton" />
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default AgreementModal
